import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import moment from 'moment';
import { INPUT_MASK_VALIDATION } from 'Src/consts/date';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  getAllTournamentParticipants,
  getConfirmationRequestTemplate,
  getTournamentParticipants,
  getTournamentParticipantsCount,
  removeTournamentParticipant,
  sendConfirmationRequest
} from 'Src/helpers/service/adminSU/tournamentParticipantsTable';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { SendRequestPageShell } from 'Src/components/SendRequestPageShell/SendRequestPageShell';
import { StatusModal } from 'Src/components/StatusModal/StatusModal';
import { getTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { ConfirmationRequestForm } from 'Src/components/ConfirmationRequestForm/ConfirmationRequestForm';
import { Tournament, TournamentParticipant } from 'Src/models/tournament';
import { SimpleAlert } from 'Src/components/SimpleAlert/SimpleAlert';
import { ConfirmationRequestTemplate } from '../../../../models/confirmationRequestTemplate';
import { INVITE_KIND } from 'Src/consts/invite';
import { INVITE_KIND_TYPE } from 'Src/types/invite';
import { FormDefinition } from 'Src/types/formSchema';
import { getAllTournamentConfirmationRequests } from 'Src/helpers/service/adminSU/tournamentConfirmationRequestsTable';
import { FormRendererV2 } from 'Src/components/FormRendererV2/FormRendererV2';
import { answersToMap } from 'Src/helpers/form/formRuntime';

const SEND_TOURNAMENT_CONFIRMATION_QUERY_PARAM = 'sendTournamentConfirmationRequests';

const statusLabel = (status?: string): string => {
  switch (status) {
    case 'NOT_READY':
      return 'Awaiting response';
    case 'ACCEPTED':
      return 'Accepted';
    case 'REJECTED':
      return 'Rejected';
    case 'OUTDATED':
      return 'Outdated';
    default:
      return status ? status : '—';
  }
};

const statusTone = (status?: string): { text: string; bg: string; border: string } => {
  switch (status) {
    case 'ACCEPTED':
      return { text: '#155724', bg: '#d4edda', border: '#c3e6cb' };
    case 'REJECTED':
      return { text: '#721c24', bg: '#f8d7da', border: '#f5c6cb' };
    case 'PENDING':
      return { text: '#856404', bg: '#fff3cd', border: '#ffeeba' };
    case 'OUTDATED':
    case 'EXPIRED':
      return { text: '#0c5460', bg: '#d1ecf1', border: '#bee5eb' };
    case 'NOT_READY':
      return { text: '#6c757d', bg: '#f1f3f5', border: '#e9ecef' };
    default:
      return { text: '#6c757d', bg: '#f8f9fa', border: '#e9ecef' };
  }
};

const pad2 = (n: number) => (String(n).length >= 2 ? String(n) : `0${n}`);
const formatDateTime = (d: Date) =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const formatDeadlineShort = (deadlineIso?: string | null) => {
  if (!deadlineIso) return '—';

  const d = new Date(deadlineIso);
  if (Number.isNaN(d.getTime())) return '—';

  const now = new Date();
  const isFuture = d.getTime() > now.getTime();

  return `${formatDateTime(d)}${isFuture ? ' ⚡' : ''}`;
};

const formatList = (lines: string[], limit = 10): string => {
  if (!lines.length) return '—';
  const head = lines
    .slice(0, limit)
    .map(l => `• ${l}`)
    .join('\n');
  const tail = lines.length > limit ? `\n• …and ${lines.length - limit} more` : '';
  return head + tail;
};

const formatSchoolName = (item: any) => item?.school?.name || 'Unknown school';

const getStatusActionLabel = (status?: string): string => {
  switch (status) {
    case 'ACCEPTED':
      return 'accepted at';
    case 'REJECTED':
      return 'rejected at';
    default:
      return 'actioned at';
  }
};

const formatSkippedLine = (item: any, latestBySchoolId: LatestReqBySchoolId) => {
  const name = formatSchoolName(item);

  const status = getLatestStatus(latestBySchoolId, item.schoolId);
  const reason = statusToReason(status);

  if (shouldShowActionedAt(status)) {
    const actionedAt = getLatestActionedAt(latestBySchoolId, item.schoolId);
    const actionedAtText = actionedAt ? formatDateTime(new Date(actionedAt)) : '—';
    const actionLabel = getStatusActionLabel(status);

    return `${name} (${reason}; ${actionLabel}: ${actionedAtText})`;
  }

  const deadline = shouldShowDeadline(latestBySchoolId, item.schoolId)
    ? getLatestDeadline(latestBySchoolId, item.schoolId)
    : null;

  const deadlineText = shouldShowDeadline(latestBySchoolId, item.schoolId) ? formatDeadlineShort(deadline) : '—';

  return `${name} (${reason}; deadline: ${deadlineText})`;
};

const buildSkippedReport = (skipped: any[], latestBySchoolId: LatestReqBySchoolId): string => {
  if (!skipped.length) return '';

  const groups: Record<string, string[]> = {};

  skipped.forEach(item => {
    const status = getLatestStatus(latestBySchoolId, item.schoolId);
    const key = getSkippedGroupKey(status);
    const line = formatSkippedLine(item, latestBySchoolId);

    groups[key] = groups[key] || [];
    groups[key].push(line);
  });

  const parts = skippedGroupOrder
    .filter(k => groups[k] && groups[k].length)
    .map(k => {
      const lines = groups[k].slice().sort((a, b) => a.localeCompare(b));
      return `🔸 ${k} (${lines.length})\n${formatList(lines)}`;
    });

  Object.keys(groups).forEach(k => {
    if (skippedGroupOrder.includes(k as any)) return;
    const lines = groups[k].slice().sort((a, b) => a.localeCompare(b));
    parts.push(`🔸 ${k} (${lines.length})\n${formatList(lines)}`);
  });

  return `\n\nSkipped breakdown:\n${parts.join('\n\n')}`;
};

type SkippedGroupKey = 'Already responded' | 'Awaiting response' | 'Not sent yet' | 'Outdated request' | 'Other';

const getSkippedGroupKey = (status?: string): SkippedGroupKey => {
  switch (status) {
    case 'ACCEPTED':
    case 'REJECTED':
      return 'Already responded';

    case 'NOT_READY':
      return 'Awaiting response';

    case 'OUTDATED':
      return 'Outdated request';

    default:
      return 'Other';
  }
};

const skippedGroupOrder: SkippedGroupKey[] = [
  'Awaiting response',
  'Already responded',
  'Outdated request',
  'Not sent yet',
  'Other'
];

function shouldShowDeadline(latestBySchoolId: LatestReqBySchoolId, schoolId: any): boolean {
  const deadline = getLatestDeadline(latestBySchoolId, schoolId);
  return !!deadline;
}

function getMsgTs(m: any): number {
  const d = m?.invitationStatusSetAt || m?.updatedAt || m?.createdAt;
  const t = d ? new Date(d).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

type LatestReqBySchoolId = Record<string, any>;

function buildLatestRequestBySchoolId(messages: any[]): LatestReqBySchoolId {
  const latestBySchoolId: LatestReqBySchoolId = {};

  (messages || []).forEach(m => {
    const schoolId = String(m?.participantSchoolId || '');
    if (!schoolId) return;

    if (!latestBySchoolId[schoolId] || getMsgTs(m) > getMsgTs(latestBySchoolId[schoolId])) {
      latestBySchoolId[schoolId] = m;
    }
  });

  return latestBySchoolId;
}

const getLatestMessage = (latestBySchoolId: LatestReqBySchoolId, schoolId: any) => {
  return latestBySchoolId[String(schoolId)] || null;
};

const getLatestStatus = (latestBySchoolId: LatestReqBySchoolId, schoolId: any): string => {
  const m = getLatestMessage(latestBySchoolId, schoolId);
  return String(m?.invitationStatus || '');
};

const getLatestDeadline = (latestBySchoolId: LatestReqBySchoolId, schoolId: any): string | null => {
  const m = getLatestMessage(latestBySchoolId, schoolId);
  return m?.deadlineForAnswers || null;
};

const statusToReason = (status?: string): string => {
  switch (status) {
    case 'NOT_READY':
      return 'Awaiting response';
    case 'ACCEPTED':
      return 'Already accepted';
    case 'REJECTED':
      return 'Already rejected';
    case 'OUTDATED':
      return 'Outdated request exists';
    default:
      return status ? `Status: ${status}` : 'No previous request';
  }
};

function isResendableStatus(status?: string): boolean {
  return !status || status === 'NOT_READY' || status === 'OUTDATED' || status === 'PENDING';
}

const getLatestActionedAt = (latestBySchoolId: LatestReqBySchoolId, schoolId: any): string | null => {
  const m = getLatestMessage(latestBySchoolId, schoolId);
  return m?.invitationStatusSetAt || m?.updatedAt || null;
};

function shouldShowActionedAt(status?: string): boolean {
  return status === 'ACCEPTED' || status === 'REJECTED';
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['school', 'name'],
    width: '38%',
    minWidth: '220px'
  }
];

interface State {
  items: TournamentParticipant[];
  currentPage: number;
  selectedItems: TournamentParticipant[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  confirmationRequestTemplate: ConfirmationRequestTemplate;
  isConfirmationRequestModal: boolean;
  tournament: Tournament;
  isSchoolsDeleteInfoModalShow: boolean;
  isError: boolean;
  isInfoModalOpen: boolean;
  infoModalText: string;
  latestReqBySchoolId: Record<string, any>;
  isLastRequestModalOpen: boolean;
  lastRequestMessage: any | null;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class TournamentParticipants extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      confirmationRequestTemplate: undefined,
      isConfirmationRequestModal: false,
      isSchoolsDeleteInfoModalShow: false,
      isError: false,
      tournament: undefined,
      isInfoModalOpen: false,
      infoModalText: '',
      latestReqBySchoolId: {},
      isLastRequestModalOpen: false,
      lastRequestMessage: null
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  getTournamentId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    const tournamentId = search.tournament;
    return tournamentId;
  }

  setAdditionalItems() {
    const { user } = this.props;

    const tournamentId = this.getTournamentId();

    return getTournament(user, tournamentId).then(tournament => {
      this.setState({
        tournament: tournament
      });

      return true;
    });
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters, { isCorrectTime: true });

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const tournamentId = this.getTournamentId();

    const getItemsPromise = getTournamentParticipants(user, tournamentId, serverQueryFilter);
    const getItemsCountPromise = getTournamentParticipantsCount(user, tournamentId, where);

    const getLatestReqPromise = getAllTournamentConfirmationRequests(user, tournamentId);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all([getItemsCountPromise, getItemsPromise, getLatestReqPromise]).then(
      ([countObj, items, msgs]) => {
        const latestReqBySchoolId = buildLatestRequestBySchoolId(msgs);
        this.setState({
          currentPage: page,
          sortDirection: sortDirection,
          sortColumnsName: sortColumnsName,
          isShowFilter: isShowFilter,
          isDataFiltered: isDataFiltered,
          filters: filters,
          items: items,
          total: countObj.count,
          latestReqBySchoolId,
          basePath: history.location.pathname,
          isLoading: false
        });

        return true;
      }
    );
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;
    const { history } = this.props;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    const tournamentId = this.getTournamentId();

    getAllTournamentParticipants(user, tournamentId, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const { history } = this.props;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const { history } = this.props;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}&${search.join('&')}`,
      state: history.location.state
    });
  };

  onClearFilterClick = (): void => {
    const { history } = this.props;
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournament=${tournamentId}`,
      state: history.location.state
    });
  };

  onCancelFilterClick = (): void => {
    this.setState({ isShowFilter: false, filters: getFilters2(COLUMNS, parse(this.props.history.location.search)) });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  openLastRequestModal = (schoolId: any) => {
    const msg = getLatestMessage(this.state.latestReqBySchoolId, schoolId);

    this.setState({
      isLastRequestModalOpen: true,
      lastRequestMessage: msg || null
    });
  };

  closeLastRequestModal = () => {
    this.setState({
      isLastRequestModalOpen: false,
      lastRequestMessage: null
    });
  };

  isSchoolsDeleteModalClose = () => {
    this.setState({
      isSchoolsDeleteInfoModalShow: false
    });
  };

  isSchoolsDeleteModalOpen = (): void => {
    this.setState({
      isSchoolsDeleteInfoModalShow: true
    });
  };

  removeTournamentParticipants = () => {
    this.setState({
      isLoading: true
    });

    const { selectedItems } = this.state;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    BPromise.each(selectedItems, (item, index) => {
      return removeTournamentParticipant(user, tournamentId, item.id);
    })
      .then(res => {
        this.setState({
          isSchoolsDeleteInfoModalShow: false,
          selectedItems: []
        });
        this.setItems();
      })
      .catch(error => {
        console.error(error);
        this.setState({
          isSchoolsDeleteInfoModalShow: false,
          isError: true
        });
        this.setItems();
      });
  };

  isSendTournamentConfirmationPageOpen = (): boolean => {
    return new URLSearchParams(this.props.location.search).get(SEND_TOURNAMENT_CONFIRMATION_QUERY_PARAM) === '1';
  };

  openSendTournamentConfirmationPage = (): void => {
    const { history, location } = this.props;
    const params = new URLSearchParams(location.search);
    params.set(SEND_TOURNAMENT_CONFIRMATION_QUERY_PARAM, '1');

    history.push({
      pathname: location.pathname,
      search: `?${params.toString()}`
    });
  };

  closeSendTournamentConfirmationPage = (): void => {
    const { history, location } = this.props;
    const params = new URLSearchParams(location.search);
    params.delete(SEND_TOURNAMENT_CONFIRMATION_QUERY_PARAM);
    const search = params.toString();

    history.push({
      pathname: location.pathname,
      search: search ? `?${search}` : ''
    });
  };
  sendConfirmationRequest = () => {
    const { user } = this.props;

    this.setState({
      isLoading: true
    });

    getConfirmationRequestTemplate(user).then(confirmationRequestTemplate => {
      this.setState(
        {
          isLoading: false,
          confirmationRequestTemplate: confirmationRequestTemplate,
          isConfirmationRequestModal: true
        },
        this.openSendTournamentConfirmationPage
      );
    });
  };

  confirmationRequestModalClose = (): void => {
    this.setState({
      isConfirmationRequestModal: false
    });
    this.closeSendTournamentConfirmationPage();
  };

  onSendConfirmationRequestClick = (fields, deadlineForAnswers?: string, formDefinition?: FormDefinition) => {
    this.closeSendTournamentConfirmationPage();
    this.setState({
      isConfirmationRequestModal: false,
      isLoading: true
    });

    const { selectedItems } = this.state;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    const raw = (deadlineForAnswers || '').trim();

    let deadlineForAnswersIso: string | null = null;

    if (raw) {
      const m = moment(raw, INPUT_MASK_VALIDATION, true);
      if (m.isValid() && m.isAfter(moment())) {
        deadlineForAnswersIso = m.toDate().toISOString();
      }
    }

    getAllTournamentConfirmationRequests(user, tournamentId)
      .then(messages => {
        const latestReqBySchoolId = buildLatestRequestBySchoolId(messages);

        const sendable = selectedItems.filter(item => {
          const status = getLatestStatus(latestReqBySchoolId, item.schoolId);
          return isResendableStatus(status);
        });

        const skipped = selectedItems.filter(item => !sendable.includes(item));

        if (sendable.length === 0) {
          this.setState({
            isLoading: false,
            isInfoModalOpen: true,
            infoModalText:
              `Nothing was sent.\n\n` +
              `All selected schools already have an active request or have already responded.` +
              buildSkippedReport(skipped, latestReqBySchoolId)
          });

          return null;
        }

        return BPromise.all(
          sendable.map(item => {
            const dataToTransfer = {
              participantSchoolId: item.schoolId,
              fields,
              ...(formDefinition
                ? {
                    formSchemaVersion: 2,
                    formDefinition
                  }
                : {}),
              deadlineForAnswers: deadlineForAnswersIso
            };

            return sendConfirmationRequest(user, tournamentId, dataToTransfer);
          })
        )
          .then(() => {
            return getAllTournamentConfirmationRequests(user, tournamentId);
          })
          .then(freshMessages => {
            const freshLatestReqBySchoolId = buildLatestRequestBySchoolId(freshMessages);
            return { sendable, skipped, latestReqBySchoolId: freshLatestReqBySchoolId };
          });
      })
      .then(result => {
        if (!result) return;

        const { sendable, skipped, latestReqBySchoolId } = result;

        const sentLines = sendable
          .map(item => formatSchoolName(item))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        const titleLine =
          `Confirmation requests processed.\n\n` + `✅ Sent: ${sendable.length}\n` + `⏭ Skipped: ${skipped.length}`;

        const sentBlock = `\n\nSent to:\n${formatList(sentLines)}`;

        const skippedBlock = buildSkippedReport(skipped, latestReqBySchoolId);

        this.setState({
          isLoading: false,
          isInfoModalOpen: true,
          infoModalText: titleLine + sentBlock + skippedBlock,
          selectedItems: [],
          isSelectAllChecked: false,
          latestReqBySchoolId
        });
      })
      .catch(err => {
        const msg = err?.response?.data?.details?.text || err?.message || 'Failed to send confirmation requests.';
        this.setState({
          isLoading: false,
          isInfoModalOpen: true,
          infoModalText: msg
        });
      });
  };

  onCloseErrorClick = (event: Event) => {
    event.preventDefault();
    this.setState({
      isError: false
    });
  };

  closeInfoModal = () => {
    this.setState({
      isInfoModalOpen: false,
      infoModalText: ''
    });
  };

  renderInfoModal(): React.ReactNode {
    const { isInfoModalOpen, infoModalText } = this.state;

    const text = String(infoModalText || '');

    const sentMatch = text.match(/✅ Sent:\s*(\d+)/);
    const skippedMatch = text.match(/⏭ Skipped:\s*(\d+)/);

    const sentCount = sentMatch ? Number(sentMatch[1]) : 0;
    const skippedCount = skippedMatch ? Number(skippedMatch[1]) : 0;

    const title =
      sentCount > 0
        ? sentCount === 1
          ? 'Confirmation request sent successfully'
          : 'Confirmation requests sent successfully'
        : 'Confirmation requests';

    const message =
      sentCount > 0
        ? skippedCount > 0
          ? `${sentCount} requests were sent. ${skippedCount} selected schools were skipped.`
          : sentCount === 1
          ? 'The confirmation request was sent successfully.'
          : `${sentCount} confirmation requests were sent successfully.`
        : text || 'No requests were sent.';

    const allLines = text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    const sentToIndex = allLines.findIndex(line => line === 'Sent to:');
    const skippedIndex = allLines.findIndex(line => line === 'Skipped breakdown:');

    const sentLines =
      sentToIndex >= 0
        ? allLines
            .slice(sentToIndex + 1, skippedIndex >= 0 ? skippedIndex : undefined)
            .map(line => line.replace(/^•\s*/, '').trim())
            .filter(Boolean)
        : [];

    const skippedSectionLines =
      skippedIndex >= 0
        ? allLines
            .slice(skippedIndex + 1)
            .map(line => line.trim())
            .filter(Boolean)
        : [];

    const skippedGroups: Array<{ title: string; items: string[] }> = [];
    let currentGroup: { title: string; items: string[] } | null = null;

    skippedSectionLines.forEach(line => {
      if (line.startsWith('🔸 ')) {
        if (currentGroup) {
          skippedGroups.push(currentGroup);
        }

        currentGroup = {
          title: line.replace(/^🔸\s*/, '').trim(),
          items: []
        };

        return;
      }

      const cleanLine = line.replace(/^•\s*/, '').trim();

      if (!cleanLine) return;

      if (currentGroup) {
        currentGroup.items.push(cleanLine);
      }
    });

    if (currentGroup) {
      skippedGroups.push(currentGroup);
    }

    return (
      <StatusModal
        isOpen={isInfoModalOpen}
        status={sentCount > 0 ? 'success' : 'info'}
        title={title}
        message={message}
        onClose={this.closeInfoModal}
      >
        <div>
          {sentLines.length > 0 ? (
            <div
              style={{
                border: '1px solid #e9ecef',
                borderRadius: 8,
                padding: 16,
                marginBottom: skippedGroups.length > 0 ? 16 : 0,
                background: '#fafbfc'
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10, color: '#1f2d3d' }}>Sent to</div>

              <ul style={{ margin: 0, paddingLeft: 20, color: '#495057' }}>
                {sentLines.map((line, index) => (
                  <li key={`sent_${index}`} style={{ marginBottom: 6 }}>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {skippedGroups.length > 0 ? (
            <div
              style={{
                border: '1px solid #e9ecef',
                borderRadius: 8,
                padding: 16,
                background: '#fafbfc'
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, color: '#1f2d3d' }}>Skipped breakdown</div>

              {skippedGroups.map((group, groupIndex) => (
                <div
                  key={`group_${groupIndex}`}
                  style={{ marginBottom: groupIndex < skippedGroups.length - 1 ? 16 : 0 }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#34495e' }}>{group.title}</div>

                  <ul style={{ margin: 0, paddingLeft: 20, color: '#6c757d' }}>
                    {group.items.map((item, itemIndex) => (
                      <li key={`group_${groupIndex}_item_${itemIndex}`} style={{ marginBottom: 6 }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </StatusModal>
    );
  }

  renderConfirmationRequestPage(): React.ReactNode {
    const { confirmationRequestTemplate } = this.state;
    const { location } = this.props;

    if (!this.isSendTournamentConfirmationPageOpen()) {
      return null;
    }

    const inviteKind = propz.get(location, ['state', 'inviteKind']);

    return (
      <SendRequestPageShell
        title="Send confirmation requests"
        subtitle="Review and edit the confirmation questions before sending them to selected schools."
        onBack={this.confirmationRequestModalClose}
      >
        <ConfirmationRequestForm
          templateType="TOURNAMENT_CONFIRMATION"
          user={this.props.user}
          template={confirmationRequestTemplate}
          onCancelClick={this.confirmationRequestModalClose}
          onSendConfirmationRequestsClick={this.onSendConfirmationRequestClick}
          inviteKind={inviteKind as INVITE_KIND_TYPE}
          isRenderDeadline={true}
          defaultBuilderMode="FULL"
        />
      </SendRequestPageShell>
    );
  }
  renderLastRequestModal(): React.ReactNode {
    const { isLastRequestModalOpen, lastRequestMessage } = this.state;

    if (!isLastRequestModalOpen) return null;

    const msg = lastRequestMessage;

    if (!msg) {
      return (
        <SimpleModal
          isOpen={true}
          title="Last confirmation request"
          body="No requests found for this school."
          isBodyBold={false}
          buttonText="Close"
          onButtonClick={this.closeLastRequestModal}
          onCloseClick={this.closeLastRequestModal}
          hideCancelButton={true}
        />
      );
    }

    const status = String(msg?.invitationStatus || '');
    const deadline = msg?.deadlineForAnswers || null;

    const sentAt = msg?.createdAt ? formatDateTime(new Date(msg.createdAt)) : '—';
    const updatedAt = msg?.updatedAt ? formatDateTime(new Date(msg.updatedAt)) : '—';
    const sender = msg?.sender?.fullName || '—';

    const fields = Array.isArray(msg?.fields) ? msg.fields : [];
    const hasV2Answers = !!msg?.formDefinition;
    const hasAnyAnswer = hasV2Answers || fields.some((f: any) => typeof f?.value !== 'undefined');

    const valueToText = (f: any) => {
      if (typeof f?.value === 'undefined') return '—';
      if (f.type === 'BOOLEAN') return String(f.value) === 'true' ? 'Yes' : 'No';
      return String(f.value);
    };

    return (
      <SimpleModal
        isOpen={true}
        title={`Last request: ${msg?.participantSchoolData?.name || 'School'}`}
        isBodyBold={false}
        buttonText="Close"
        onButtonClick={this.closeLastRequestModal}
        onCloseClick={this.closeLastRequestModal}
        hideCancelButton={true}
      >
        <div style={{ lineHeight: 1.4 }}>
          <div>
            <span style={{ fontWeight: 700 }}>Status:</span> {statusLabel(status)}
          </div>

          <div>
            <span style={{ fontWeight: 700 }}>Sent:</span> {sentAt}
            <span style={{ marginLeft: 12 }} />
            <span style={{ fontWeight: 700 }}>Updated:</span> {updatedAt}
          </div>

          <div>
            <span style={{ fontWeight: 700 }}>Sender:</span> {sender}
          </div>

          <div>
            <span style={{ fontWeight: 700 }}>Deadline:</span> {deadline ? formatDateTime(new Date(deadline)) : '—'}
          </div>

          <hr />

          <div style={{ fontWeight: 700, marginBottom: 8 }}>Questions & answers</div>

          {hasV2Answers ? (
            <FormRendererV2
              definition={msg.formDefinition}
              answers={answersToMap(msg.formAnswers || [])}
              disabled
              onChange={() => undefined}
            />
          ) : null}

          {!hasAnyAnswer && (
            <div style={{ color: '#6c757d' }}>
              No answers recorded yet (or the response type does not store answers).
            </div>
          )}

          {!hasV2Answers && fields.map((f: any, idx: number) => (
            <div key={String(f?._id || idx)} style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
              <div style={{ fontWeight: 600 }}>
                {f?.heading || 'Question'}
                {f?.isRequired ? <span style={{ marginLeft: 6, color: '#dc3545' }}>*</span> : null}
              </div>

              <div style={{ color: '#6c757d', fontSize: 13 }}>
                Type: {f?.type || '—'}
                {Array.isArray(f?.enumOptions) && f.enumOptions.length > 0
                  ? ` • Options: ${f.enumOptions.join(', ')}`
                  : ''}
              </div>

              <div>
                <span style={{ fontWeight: 700 }}>Answer:</span> {valueToText(f)}
              </div>
            </div>
          ))}
        </div>
      </SimpleModal>
    );
  }

  renderSchoolsDeleteInfoModal(): React.ReactNode {
    const { selectedItems, isSchoolsDeleteInfoModalShow } = this.state;
    const schoolNames = selectedItems.map(participants => participants.school.name).join(', ');
    const infoText = `You are removing schools from the tournament: ${schoolNames}.\nAll relevant teams will be also removed. Continue?`;

    return (
      <SimpleModal
        isOpen={isSchoolsDeleteInfoModalShow}
        title={'Remove from tournament'}
        body={infoText}
        buttonText={'Remove'}
        onButtonClick={this.removeTournamentParticipants}
        onCloseClick={this.isSchoolsDeleteModalClose}
      />
    );
  }

  renderSendingError(): React.ReactNode {
    const { isError } = this.state;
    if (!isError) {
      return null;
    }

    const errorMessage =
      'The school you are trying to remove is taking part in some events within the tournament. ' +
      'Please, remove those events first to be able to exclude the school from the competition.';

    return (
      <SimpleAlert
        message={errorMessage}
        additionalClassNames={'alert-danger'}
        onCloseAlertClick={this.onCloseErrorClick}
      />
    );
  }

  goBack = () => {
    const { history, location } = this.props;
    const { state } = location;
    const { search, prevPath } = state as any;
    const tournamentId = this.getTournamentId();

    history.push({
      pathname: prevPath,
      search: search,
      state: { tournamentId: tournamentId }
    });
  };

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      tournament,
      latestReqBySchoolId
    } = this.state;

    const actionItems = [
      {
        itemText: 'Send confirmation request',
        onItemClick: this.sendConfirmationRequest,
        isActive: selectedItems.length > 0
      },
      {
        itemText: 'Remove from tournament',
        onItemClick: this.isSchoolsDeleteModalOpen,
        isActive: selectedItems.length > 0
      }
    ];

    const filterOptions = {};
    const gridTitle = typeof tournament !== 'undefined' ? `${tournament.name} / Participants` : '';

    const columns: ColumnDefinition[] = [
      ...COLUMNS,
      {
        text: 'Request status',
        field: 'crStatus',
        isSort: false,
        type: FILTER_TYPE.NONE,
        width: '24%',
        minWidth: '250px',
        cell: (p: any): JSX.Element => {
          const status = getLatestStatus(latestReqBySchoolId, p.schoolId);
          const tone = statusTone(status);
          const hasMsg = !!getLatestMessage(latestReqBySchoolId, p.schoolId);

          return (
            <td style={{ width: '240px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  title={status ? `Latest status: ${status}` : 'No requests yet'}
                  style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    color: tone.text,
                    background: tone.bg,
                    border: `1px solid ${tone.border}`
                  }}
                >
                  {statusLabel(status)}
                </span>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={!hasMsg}
                  onClick={() => this.openLastRequestModal(p.schoolId)}
                  title={hasMsg ? 'View last request' : 'No requests yet'}
                >
                  View
                </button>
              </div>
            </td>
          );
        }
      },
      {
        text: 'Responded at',
        field: 'crRespondedAt',
        isSort: false,
        type: FILTER_TYPE.NONE,
        width: '20%',
        minWidth: '220px',
        cell: (p: any): JSX.Element => {
          const status = getLatestStatus(latestReqBySchoolId, p.schoolId);

          if (!shouldShowActionedAt(status)) {
            return <td style={{ width: '220px' }}>—</td>;
          }

          const raw = getLatestActionedAt(latestReqBySchoolId, p.schoolId);

          return <td style={{ width: '220px' }}>{raw ? formatDateTime(new Date(raw)) : '—'}</td>;
        }
      },
      {
        text: 'Deadline',
        field: 'crDeadline',
        isSort: false,
        type: FILTER_TYPE.NONE,
        width: '18%',
        minWidth: '220px',
        cell: (p: any): JSX.Element => {
          const status = getLatestStatus(latestReqBySchoolId, p.schoolId);

          if (!shouldShowDeadline(latestReqBySchoolId, p.schoolId)) {
            return <td style={{ width: '220px' }}>—</td>;
          }

          const raw = getLatestDeadline(latestReqBySchoolId, p.schoolId);
          if (!raw) {
            return <td style={{ width: '220px' }}>—</td>;
          }

          const now = new Date();
          const d = new Date(raw);
          const msLeft = d.getTime() - now.getTime();

          let icon = '⚡';
          let color = '#6c757d';

          if (msLeft > 72 * 3600 * 1000) color = '#2f8f3a';
          else if (msLeft > 24 * 3600 * 1000) color = '#d39e00';
          else if (msLeft >= 0) color = '#dc3545';
          else {
            icon = '⛔';
            color = '#6c757d';
          }

          const showCountdown = msLeft >= 0 && msLeft < 2 * 3600 * 1000;
          const totalMinutes = Math.max(0, Math.floor(msLeft / 60000));
          const h = Math.floor(totalMinutes / 60);
          const m = totalMinutes % 60;
          const countdown = showCountdown ? ` (${h > 0 ? `${h}h ` : ''}${m}m left)` : '';

          return (
            <td style={{ width: '220px' }}>
              <span title={msLeft >= 0 ? 'Deadline approaching' : 'Deadline passed'}>
                {formatDateTime(d)} <span style={{ color, fontWeight: 700 }}>{icon}</span>
                {countdown && <span style={{ marginLeft: '6px', fontWeight: 600 }}>{countdown}</span>}
              </span>
            </td>
          );
        }
      }
    ];

    return (
      <Grid2
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={columns}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onCancelFilterClick={this.onCancelFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        gridTitle={gridTitle}
        goBack={this.goBack}
        user={this.props.user}
      />
    );
  }

  render() {
    const { isLoading, isConfirmationRequestModal, isSchoolsDeleteInfoModalShow, isError } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (this.isSendTournamentConfirmationPageOpen()) {
      return this.renderConfirmationRequestPage();
    }

    const classes =
      this.isSendTournamentConfirmationPageOpen() || isSchoolsDeleteInfoModalShow || isError || this.state.isInfoModalOpen
        ? 'mt-3 modal-open'
        : 'mt-3';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderSendingError()}
        {this.renderSchoolsDeleteInfoModal()}
        {this.renderConfirmationRequestPage()}
        {this.renderInfoModal()}
        {this.renderLastRequestModal()}
        <div className="row">
          <div className="col-md-12">{this.renderGrid()}</div>
        </div>
      </div>
    );
  }
}



